import { Box, Typography, Collapse, Link } from "@mui/material";
import { ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import IconLoader from "@/features/Common/components/IconLoader";
import NavigationV2 from "@/features/Common/components/NavigationV2";
import MyHealthScoreIcon from "@/theme/icons/MyHealthScoreIcon";
import MyHealthProfileIcon from "@/theme/icons/MyHealthProfileIcon";
import MyHealthRecordsIcon from "@/theme/icons/MyHealthRecordsIcon";
import theme, { NavigationConfig } from "@/theme";
import { alpha } from "@mui/system/colorManipulator";
import { useTranslation } from "react-i18next";

export interface MenuItem {
  id: string;
  icon: ReactNode;
  title: string;
  link: string;
  description: string;
}

export interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
}

export interface MainNavigationProps {
  expanded?: boolean;
  menuItems?: MenuItem[];
  navItems?: NavItem[];
  activeNavItem?: string;
  onExpandedChange?: (expanded: boolean) => void;
  onNavItemClick?: (id: string) => void;
}

/**
 * NavMenuItem - Individual menu item with icon, title, description, and chevron
 */
export function NavMenuItem({
  item,
  onClick,
}: {
  item: MenuItem;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
        cursor: "pointer",
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 47,
          height: 54,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: '"Manrope", sans-serif',
            fontSize: "16px",
            fontWeight: 600,
            color: "grey.900",
            lineHeight: 1.4,
          }}
        >
          {item.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Manrope", sans-serif',
            fontSize: "14px",
            fontWeight: 400,
            color: "grey.700",
            lineHeight: 1.4,
            mt: 0.25,
          }}
        >
          {item.description}
        </Typography>
      </Box>

      <IconLoader
        icon="ChevronNextIcon"
        sx={{
          fontSize: 24,
          color: "grey.700",
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

export default function MyHealthMenu({
  expanded = false,
  onExpandedChange,
}: MainNavigationProps) {
  const { t } = useTranslation();
  const [localExpanded, setLocalExpanded] = useState(expanded);
  const isExpanded = onExpandedChange ? expanded : localExpanded;
  const location = useLocation();
  const menuItems: MenuItem[] = [
    {
      id: "health-score",
      icon: <MyHealthScoreIcon />,
      title: t("MyHealthMenu.score.title"),
      link: "/home/health-score",
      description: t("MyHealthMenu.score.description"),
    },
    {
      id: "health-profile",
      icon: <MyHealthProfileIcon />,
      title: t("MyHealthMenu.profile.title"),
      link: "/home/my-health/health-profile",
      description: t("MyHealthMenu.profile.description"),
    },
    {
      id: "health-record",
      icon: <MyHealthRecordsIcon />,
      title: t("MyHealthMenu.record.title"),
      link: "/home/my-health/health-record",
      description: t("MyHealthMenu.record.description"),
    },
  ];

  const handleToggle = () => {
    if (onExpandedChange) {
      onExpandedChange(!isExpanded);
    } else {
      setLocalExpanded(!localExpanded);
    }
  };

  return (
    <Box>
      {/* Overlay when expanded */}
      {isExpanded && (
        <Box
          data-testid="overlay"
          onClick={handleToggle}
          sx={{
            height: "100vh",
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.6,
            bgcolor: "black",
            zIndex: 999,
          }}
        />
      )}
      <Box sx={{ position: "relative" }}>
        {/* Navigation container */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1000,
            bgcolor: "white",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: isExpanded ? "" : NavigationConfig.boxShadow,
          }}
        >
          {/* Expandable menu content */}
          <Collapse in={isExpanded}>
            <Box
              sx={{
                bgcolor: "white",
                pt: 1,
                pb: 1,
              }}
            >
              {/* Menu items */}
              {menuItems.map((item, index) => (
                <Link
                  underline="none"
                  href={item.link}
                  aria-label={item.title}
                  key={item.id}
                  sx={{
                    width: "100%",
                    px: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s ease",
                    bgcolor:
                      location.pathname === item.link
                        ? alpha(theme.palette.primary.main, 0.05)
                        : "white",
                  }}
                >
                  <Box
                    sx={{
                      py: 1,
                      borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    }}
                  >
                    <NavMenuItem item={item} />
                    {index < menuItems.length - 1 && (
                      <Box
                        sx={{
                          height: 1,
                          bgcolor: "grey.100",
                        }}
                      />
                    )}
                  </Box>
                </Link>
              ))}
            </Box>
          </Collapse>
          <NavigationV2 onNavigationClick={handleToggle} />
        </Box>
      </Box>
    </Box>
  );
}
